import { AbstractClient, CompositionClient } from "@/app/client";
import { UUID } from "crypto";

export interface DocumentResponse {
  id: UUID;
  company_id: UUID;
  name: string;
  url: string;
  document_type?: string | null;
  reference_type: string;
  reference_id: UUID;
  document_metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface LotResponse {
  id: UUID;
  company_id: UUID;
  supplier_id?: UUID | null;
  lot_number: string;
  tracking_number?: string | null;
  status: string;
  date?: string | null;
  lot_metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface LotCreate {
  supplier_id?: UUID | null;
  lot_number: string;
  tracking_number?: string | null;
  status?: string;
  date?: string | null;
  lot_metadata?: Record<string, unknown> | null;
}

export interface LotUpdate {
  supplier_id?: UUID | null;
  lot_number?: string;
  tracking_number?: string | null;
  status?: string;
  date?: string | null;
  lot_metadata?: Record<string, unknown> | null;
}

export interface LotQuickCreate {
  lot_number: string;
  supplier_id?: UUID | null;
  tracking_number?: string | null;
  date?: string | null;
}

export interface LotSearch {
  id: UUID;
  lot_number: string;
  tracking_number?: string | null;
  supplier_id?: UUID | null;
  status: string;
  created_at: string;
  stocks_count: number;
}

export interface LotSummary {
  id: UUID;
  company_id: UUID;
  supplier_id?: UUID | null;
  lot_number: string;
  tracking_number?: string | null;
  status: string;
  date?: string | null;
  lot_metadata?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  stocks_count: number;
  total_quantity: number;
}

export interface FlowCreate {
  lot_id: UUID;
  quantity: number;
  flow_type: "IN" | "OUT" | "ADJUST";
  reason?: string;
  source_location_id?: UUID | null;
}

// Full lot response with all related data
export interface LotFullStockProduct {
  id: UUID;
  name: string;
  short_name: string | null;
  sku: string;
}

export interface LotFullStockUnit {
  id: UUID;
  name: string;
  short_name: string;
}

export interface LotFullStockLocation {
  id: UUID;
  name: string;
  code: string | null;
}

export interface LotFullStockPartner {
  id: UUID;
  name: string;
}

export interface LotFullStock {
  id: UUID;
  company_id: UUID;
  product_id: UUID;
  unit_id: UUID | null;
  quantity: number;
  weight: number;
  name: string | null;
  partner_id: UUID | null;
  lot_id: UUID | null;
  location_id: UUID | null;
  quality_score: number | null;
  price: number | null;
  stock_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  product: LotFullStockProduct | null;
  unit: LotFullStockUnit | null;
  location: LotFullStockLocation | null;
  partner: LotFullStockPartner | null;
}

export interface LotFullFlow {
  id: UUID;
  company_id: UUID;
  product_id: UUID;
  unit_id: UUID | null;
  stock_id: UUID | null;
  partner_id: UUID | null;
  transaction_id: UUID | null;
  direction: string;
  flow_type: string;
  quantity: number;
  weight: number;
  price: number | null;
  provisional: boolean;
  order_date: string | null;
  delivery_date: string | null;
  comment: string | null;
  flow_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  product: LotFullStockProduct | null;
  partner: LotFullStockPartner | null;
  lot_number: string | null;
  location_name: string | null;
}

export interface LotFullTransferStock {
  id: UUID;
  name: string | null;
  weight: number;
  quantity: number;
}

export interface LotFullTransferProduct {
  id: UUID;
  name: string;
  short_name: string | null;
}

export interface LotFullTransfer {
  id: UUID;
  company_id: UUID;
  transaction_id: UUID | null;
  transfer_type: string;
  status: string;
  source_stock_id: UUID | null;
  destination_stock_id: UUID | null;
  source_product_id: UUID;
  destination_product_id: UUID;
  source_unit_id: UUID;
  destination_unit_id: UUID;
  source_quantity: number;
  destination_quantity: number;
  source_weight: number;
  destination_weight: number;
  price: number | null;
  provisional: boolean;
  order_date: string | null;
  delivery_date: string | null;
  comment: string | null;
  transfer_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  source_stock: LotFullTransferStock | null;
  destination_stock: LotFullTransferStock | null;
  source_product: LotFullTransferProduct | null;
  destination_product: LotFullTransferProduct | null;
  source_unit: LotFullStockUnit | null;
  destination_unit: LotFullStockUnit | null;
}

export interface LotFullFee {
  id: UUID;
  company_id: UUID;
  fee_type: string;
  amount: string;
  description: string | null;
  reference_type: string;
  reference_id: UUID;
  fee_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface LotFullOperation {
  id: UUID;
  company_id: UUID;
  partner_id: UUID | null;
  operation_type: string;
  status: string;
  reference_number: string | null;
  date: string | null;
  comment: string | null;
  operation_metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  partner: LotFullStockPartner | null;
}

export interface LotFullLot extends LotResponse {
  supplier: LotFullStockPartner | null;
}

export interface LotFullSummary {
  stocks_count: number;
  flows_count: number;
  transfers_count: number;
  fees_count: number;
  operations_count: number;
  total_quantity: number;
  total_weight: number;
  total_fees: number;
}

export interface LotFullResponse {
  lot: LotFullLot;
  stocks: LotFullStock[];
  flows: LotFullFlow[];
  transfers: LotFullTransfer[];
  fees: LotFullFee[];
  operations: LotFullOperation[];
  summary: LotFullSummary;
}

export default class APILots extends CompositionClient {
  constructor(client: AbstractClient) {
    super(client);
  }

  // Get all lots
  list = async (): Promise<LotResponse[]> => {
    const res = await this._fetch({
      url: `/lots/`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Get all lots with summary data (stocks count, total quantity)
  listWithSummary = async (params?: {
    product_id?: UUID;
  }): Promise<LotSummary[]> => {
    const searchParams = new URLSearchParams();
    if (params?.product_id) {
      searchParams.set("product_id", params.product_id);
    }
    const queryString = searchParams.toString();
    const url = queryString
      ? `/lots/with-summary?${queryString}`
      : `/lots/with-summary`;

    const res = await this._fetch({
      url,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Get lot by ID
  get = async (lotId: UUID): Promise<LotResponse> => {
    const res = await this._fetch({
      url: `/lots/${lotId}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Get full lot data with all related entities
  getFull = async (lotId: UUID): Promise<LotFullResponse> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/full`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Create a new lot
  create = async (lot: LotCreate): Promise<LotResponse> => {
    const res = await this._fetch({
      url: `/lots/`,
      method: "POST",
      body: lot,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Update a lot
  update = async (lotId: UUID, lot: LotUpdate): Promise<LotResponse> => {
    const res = await this._fetch({
      url: `/lots/${lotId}`,
      method: "PUT",
      body: lot,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Delete a lot
  delete = async (lotId: UUID): Promise<void> => {
    await this._fetch({
      url: `/lots/${lotId}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
  };

  // Adjust lot quantity (create a flow)
  adjust = async (lotId: UUID, flow: FlowCreate): Promise<LotResponse> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/adjust`,
      method: "POST",
      body: flow,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Get lot flows (movements)
  getFlows = async (lotId: UUID): Promise<any[]> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/flows`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Get lots by supplier
  getBySupplier = async (
    supplierId: UUID,
    skip: number = 0,
    limit: number = 100,
  ): Promise<LotResponse[]> => {
    const res = await this._fetch({
      url: `/lots/by-supplier/${supplierId}?skip=${skip}&limit=${limit}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Quick create lot
  quickCreate = async (lot: LotQuickCreate): Promise<LotResponse> => {
    const res = await this._fetch({
      url: `/lots/quick`,
      method: "POST",
      body: lot,
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Search lots
  search = async (q: string, limit: number = 20): Promise<LotSearch[]> => {
    const res = await this._fetch({
      url: `/lots/search/?q=${encodeURIComponent(q)}&limit=${limit}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Document management methods

  // Get documents for a lot
  getDocuments = async (
    lotId: UUID,
  ): Promise<{ lot_id: UUID; files: DocumentResponse[] }> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/documents`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Upload documents to a lot
  uploadDocuments = async (
    lotId: UUID,
    files: File[],
  ): Promise<{ message: string; files: DocumentResponse[]; lot_id: UUID }> => {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append("files", file);
    });

    const res = await this._fetch({
      url: `/lots/${lotId}/documents/upload`,
      method: "POST",
      body: formData,
      bodyMime: "multipart/form-data",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Download a document from a lot
  downloadDocument = async (lotId: UUID, fileUrl: string): Promise<Blob> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/documents/download?file_url=${encodeURIComponent(fileUrl)}`,
      method: "GET",
      auth: ["OAuth2PasswordBearer"],
    });
    return res.blob();
  };

  // Remove a document from a lot
  removeDocument = async (
    lotId: UUID,
    fileUrl: string,
  ): Promise<{
    message: string;
    removed_file: DocumentResponse;
    lot_id: UUID;
  }> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/documents/remove?file_url=${encodeURIComponent(fileUrl)}`,
      method: "DELETE",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };

  // Rename a document in a lot
  renameDocument = async (
    lotId: UUID,
    fileUrl: string,
    newName: string,
  ): Promise<{ message: string; new_name: string; lot_id: UUID }> => {
    const res = await this._fetch({
      url: `/lots/${lotId}/documents/rename?file_url=${encodeURIComponent(fileUrl)}`,
      method: "PUT",
      body: { new_name: newName },
      bodyMime: "application/json",
      auth: ["OAuth2PasswordBearer"],
    });
    if (res.headers.get("Content-Type") === "application/json") {
      return res.json();
    }
    throw new Error("Bad content type");
  };
}
